import { MagnifyingGlassIcon } from "@heroicons/react/24/outline"
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react"
import { toast } from "react-toastify"
import { useAdForgeGetDefaultsQuery } from "~/ui-rtk/api/adForgeApi"
import {
  ACTIVE_JOB_STATUSES,
  useGetJobQuery,
  type JobResult,
} from "~/ui-rtk/api/jobsApi"
import { useSearchAdSetsQuery } from "~/ui-rtk/api/paidMediaApi"
import {
  useLaunchPartnershipAdsMutation,
  usePreviewPartnershipLaunchMutation,
} from "~/ui-rtk/api/partnershipsApi"
import type { AdSet } from "~/ui-rtk/api/types/paid-media.types"
import type {
  PartnershipContentRowDto,
  PartnershipLaunchPreviewDto,
  PartnershipLaunchRequest,
  PartnershipLaunchTargetDto,
  PartnershipLaunchTargetResultDto,
} from "~/ui-rtk/api/types/partnerships.types"
// Direct imports (not the `common` barrel) — see CreativesTab.tsx.
import Dialog from "~/ui-rtk/components/ui/common/Dialog"
import Checkbox from "~/ui-rtk/components/ui/controls/Checkbox"
import Input from "~/ui-rtk/components/ui/controls/Input"
import AdNameBuilder from "~/ui-rtk/pages/AdForge/components/AdNameBuilder"
import { getServerErrorMessage } from "~/ui-rtk/utils/http-utils"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import {
  AD_CODE_CREATOR_STEPS,
  AD_NAME_CONVENTION_PLACEHOLDER,
  AD_NAME_MAX,
  AD_NAME_REQUIRED,
  BULK_AD_NAME_CONVENTION_NOTE,
  BULK_AD_NAME_NOTE,
  CREATE_PAUSED_ADS_LABEL,
  CTA_LABEL,
  LANDING_PAGE_INVALID,
  LAUNCH_FAILED,
  LAUNCH_IN_BACKGROUND_NOTE,
  LAUNCH_PREVIEW_REST_NOTE,
  LAUNCH_STEP_COPY,
  LAUNCH_WARNING_COPY,
  NEEDS_AD_CODE_CHIP,
  PREVIEW_FAILED,
  composePartnershipAdName,
  createdAdsToast,
  creatingPausedAdsLabel,
  describeLaunchFailure,
  describeValidation,
  hardLaunchBlockers,
  humanize,
  launchDialogTitle,
  describeTargetBlockers,
  describeMissingRequiredTags,
} from "./partnership-copy"
import { StatusChip, type ChipTone } from "./PartnershipsTable"

/**
 * Launch tracker rows as PAUSED partnership ads in existing Meta ad sets
 * (MD-5501). Three steps, then the job:
 *
 *   1. Ad sets — the same search-as-you-type picker AdBlaster uses
 *      (`GET /paid-media/meta/adsets/search`), grouped by campaign,
 *      multi-select, capped at the API's 10 and at 50 posts × ad sets.
 *   2. Details — ad name (editable for one post, server-composed for a bulk
 *      launch — led by the company's naming convention filled from the
 *      tags, when it has one, which also names one post unless its name is
 *      typed; the AdForge tag composer can build it and its tag selection
 *      rides along on every ad), landing page (row → creator → company
 *      default; https) and the fixed Shop now CTA.
 *   3. Preview — `POST /partnerships/launch/preview` dry-runs every
 *      post × ad set target against Meta with validate_only. Nothing is
 *      created. The confirm button promises no count: the job tries every
 *      target the tracker does not block, including ones Meta refused in
 *      the dry run, and reports each one.
 *
 * Confirm enqueues `partnership-ad-create`, shows how many targets the API
 * accepted, and polls the job the way the tab polls a sync
 * (`useGetJobQuery`, 4 s), then lists every target's outcome. No code path
 * here activates an ad, and nothing the API returns (targets, results) ever
 * carries an ad code — the row's code stays encrypted server-side and is
 * used inside the job only.
 *
 * Mounted only while open, so every launch starts from a clean slate.
 */

/** Mirrors the API DTO: `adsetIds` 1..10. */
export const MAX_LAUNCH_AD_SETS = 10
/**
 * Post × ad set pairs per launch. Mirrors the API's `MAX_LAUNCH_TARGETS`
 * (apps/api/src/services/partnerships/jobs/partnership-ad-create.job.ts,
 * enforced by `PartnershipLaunchService.plan`) — change both.
 */
const MAX_LAUNCH_TARGETS = 50
const SEARCH_DEBOUNCE_MS = 300
const JOB_POLL_MS = 4000

type Step = "adsets" | "details" | "preview" | "creating" | "done"

const DIALOG_CONTAINER_CLS =
  "flex max-h-[90vh] w-full max-w-[95vw] flex-col gap-3 rounded-lg border border-basic-blue bg-basic-dark-blue p-5 text-left text-white shadow-xl overflow-y-auto"

export function isHttpsUrl(value: string): boolean {
  try {
    return new URL(value).protocol === "https:"
  } catch {
    return false
  }
}

function isTargetResult(
  value: unknown,
): value is PartnershipLaunchTargetResultDto {
  if (typeof value !== "object" || value === null) return false
  const v = value as Record<string, unknown>
  return (
    typeof v.contentId === "string" &&
    typeof v.adsetId === "string" &&
    typeof v.ok === "boolean"
  )
}

/**
 * The per-target outcomes of a finished `partnership-ad-create` run
 * (`result.results[]`). Anything that is not a target result is dropped, so
 * a shape drift in the job never throws in the render path.
 */
export function readLaunchResults(
  result: JobResult | null | undefined,
): PartnershipLaunchTargetResultDto[] {
  const raw = result?.results
  return Array.isArray(raw) ? raw.filter(isTargetResult) : []
}

/**
 * Created / failed counts: from the target results when present, else from
 * the job's generic summary, else null (the job finished but said nothing
 * the dialog can count).
 */
export function summarizeLaunch(
  result: JobResult | null | undefined,
): { created: number; failed: number } | null {
  const results = readLaunchResults(result)
  if (results.length > 0) {
    return {
      created: results.filter(r => r.ok).length,
      failed: results.filter(r => !r.ok).length,
    }
  }
  const summary = result?.summary
  if (summary) return { created: summary.successful, failed: summary.failed }
  return null
}

export interface CampaignGroup {
  id: string
  name: string
  adSets: AdSet[]
}

/** Group the search results under their campaign, in result order (same layout as the AdBlaster picker). */
export function groupAdSetsByCampaign(
  adSets: AdSet[] | undefined,
): CampaignGroup[] {
  if (!adSets) return []
  const map = new Map<string, CampaignGroup>()
  for (const adSet of adSets) {
    const id = adSet.campaign?.id || adSet.campaign_id || "unknown"
    const name = adSet.campaign?.name || "Unknown campaign"
    const entry = map.get(id) ?? { id, name, adSets: [] }
    entry.adSets.push(adSet)
    map.set(id, entry)
  }
  return [...map.values()]
}

function adSetStatusTone(status: string | undefined): ChipTone {
  switch ((status ?? "").toUpperCase()) {
    case "ACTIVE":
      return "green"
    case "PAUSED":
    case "CAMPAIGN_PAUSED":
      return "amber"
    default:
      return "grey"
  }
}

function targetKey(target: { contentId: string; adsetId: string }): string {
  return `${target.contentId}:${target.adsetId}`
}

/**
 * Ready (the API's per-target verdict) / Needs creator code / Not ready — a
 * verdict on the preview, not a promise about what the job will create.
 */
function TargetChip({ target }: { target: PartnershipLaunchTargetDto }) {
  if (target.launchable) {
    return <StatusChip tone="green">Ready</StatusChip>
  }
  if (target.needsAdCode) {
    return <StatusChip tone="amber">{NEEDS_AD_CODE_CHIP}</StatusChip>
  }
  return <StatusChip tone="red">Not ready</StatusChip>
}

interface FieldProps {
  label: string
  hint?: string
  children: ReactNode
}
// A div, not a label: `Input` renders its own <label>, and nested labels are invalid.
function Field({ label, hint, children }: FieldProps) {
  return (
    <div className="flex flex-col gap-1 text-xs text-gray-300">
      <span>
        {label}
        {hint && <span className="ml-1 text-gray-500">({hint})</span>}
      </span>
      {children}
    </div>
  )
}

function plural(n: number, noun: string): string {
  return `${n} ${noun}${n === 1 ? "" : "s"}`
}

export interface PartnershipLaunchDialogProps {
  /**
   * Rows to launch — the tab passes only rows with no blockers but
   * `video_needs_ad_code`, which the preview's dry run decides.
   */
  rows: PartnershipContentRowDto[]
  onClose: () => void
  /**
   * The create job reached a terminal state, or the dialog was closed while
   * it was still running: the tab refetches the overview so the Ads column
   * picks up the new PAUSED ads.
   */
  onLaunched: () => void
}

export default function PartnershipLaunchDialog({
  rows,
  onClose,
  onLaunched,
}: PartnershipLaunchDialogProps) {
  const single = rows.length === 1 ? rows[0] : null
  const [step, setStep] = useState<Step>("adsets")
  const [error, setError] = useState<string | null>(null)

  // ----- Step 1: ad sets -----
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  useEffect(() => {
    const id = window.setTimeout(
      () => setDebouncedSearch(search.trim()),
      SEARCH_DEBOUNCE_MS,
    )
    return () => window.clearTimeout(id)
  }, [search])
  const { data: adSets, isFetching: adSetsLoading } = useSearchAdSetsQuery({
    platform: "meta",
    search: debouncedSearch || undefined,
  })
  const groups = useMemo(() => groupAdSetsByCampaign(adSets), [adSets])
  const [selectedAdSets, setSelectedAdSets] = useState<Map<string, AdSet>>(
    () => new Map(),
  )
  // Every post goes into every ad set, so more posts leave room for fewer
  // ad sets under the pair cap.
  const adSetCap = Math.min(
    MAX_LAUNCH_AD_SETS,
    Math.floor(MAX_LAUNCH_TARGETS / rows.length),
  )
  const atCap = selectedAdSets.size >= adSetCap
  const toggleAdSet = useCallback(
    (adSet: AdSet) => {
      setSelectedAdSets(prev => {
        const next = new Map(prev)
        const id = String(adSet.id)
        if (next.has(id)) next.delete(id)
        else if (next.size < adSetCap) next.set(id, adSet)
        return next
      })
    },
    [adSetCap],
  )
  const adCount = rows.length * selectedAdSets.size

  // ----- Step 2: details -----
  const { data: defaults } = useAdForgeGetDefaultsQuery()
  const hasNameTemplate = !!defaults?.nameTemplate?.trim()
  // The name typed or applied from the tag composer; null until then.
  const [adName, setAdName] = useState<string | null>(null)
  // A typed name is the request's `adName`, used as is. Otherwise a company
  // with a naming convention leaves the name to the server (convention ·
  // handle · rights · expiry, which the preview shows); without one the
  // field holds the server's default and sends it.
  const [adNameTouched, setAdNameTouched] = useState(false)
  const adNameValue =
    adName ??
    (single && !hasNameTemplate ? composePartnershipAdName(single) : "")
  const serverNamesAd =
    hasNameTemplate && (!adNameTouched || !adNameValue.trim())
  const [builderOpen, setBuilderOpen] = useState(false)
  // Tag selection from the composer (category slug → option ids); applied
  // to every ad the job creates. Sent only when something was picked.
  const [tags, setTags] = useState<Record<string, string[]>>({})
  const tagCount = Object.values(tags).reduce((n, ids) => n + ids.length, 0)
  // Row landing page → creator's → company AdForge default: the chain the
  // API applies, shown so the user sees (and can change) the link instead of
  // the job silently picking one. Bulk: empty means "each row's own, else the
  // company default" server-side.
  const defaultLandingPage = single
    ? (single.landingPageUrl ??
      single.partnership?.landingPageUrl ??
      defaults?.destinationUrl ??
      "")
    : ""
  const [landingPage, setLandingPage] = useState<string | null>(null)
  const landingValue = landingPage ?? defaultLandingPage

  const buildRequest = useCallback((): PartnershipLaunchRequest => {
    const body: PartnershipLaunchRequest = {
      contentIds: rows.map(r => r.id),
      adsetIds: [...selectedAdSets.keys()],
    }
    if (single && !serverNamesAd && adNameValue.trim()) {
      body.adName = adNameValue.trim()
    }
    const link = landingValue.trim()
    if (link) body.landingPageUrl = link
    if (tagCount > 0) body.tags = tags
    return body
  }, [
    rows,
    selectedAdSets,
    single,
    serverNamesAd,
    adNameValue,
    landingValue,
    tags,
    tagCount,
  ])

  // ----- Step 3: preview (validate_only — creates nothing) -----
  const [previewLaunch, { isLoading: previewing }] =
    usePreviewPartnershipLaunchMutation()
  const [preview, setPreview] = useState<PartnershipLaunchPreviewDto | null>(
    null,
  )
  const runPreview = useCallback(async () => {
    if (single && !serverNamesAd && !adNameValue.trim()) {
      setError(AD_NAME_REQUIRED)
      return
    }
    const link = landingValue.trim()
    if (link && !isHttpsUrl(link)) {
      setError(LANDING_PAGE_INVALID)
      return
    }
    setError(null)
    try {
      setPreview(await previewLaunch(buildRequest()).unwrap())
      setStep("preview")
    } catch (e) {
      setError(getServerErrorMessage(e, PREVIEW_FAILED))
    }
  }, [
    single,
    serverNamesAd,
    adNameValue,
    landingValue,
    previewLaunch,
    buildRequest,
  ])

  const targetsByKey = useMemo(() => {
    const map = new Map<string, PartnershipLaunchTargetDto>()
    for (const target of preview?.targets ?? []) {
      map.set(targetKey(target), target)
    }
    return map
  }, [preview])

  const labelFor = useCallback(
    (result: { contentId: string; adsetId: string }): string => {
      const target = targetsByKey.get(targetKey(result))
      return target
        ? `${target.adName} → ${target.adsetName ?? result.adsetId}`
        : `${result.contentId} → ${result.adsetId}`
    },
    [targetsByKey],
  )

  // ----- Confirm: enqueue the job, poll it to a terminal state -----
  const [launchAds, { isLoading: launching }] =
    useLaunchPartnershipAdsMutation()
  const [jobId, setJobId] = useState<string | null>(null)
  // Targets the API accepted: every pre-flight-ok one, which can be more
  // than the preview's "Ready" ones.
  const [accepted, setAccepted] = useState(0)
  const { data: job } = useGetJobQuery(jobId ?? "", {
    skip: !jobId,
    pollingInterval: jobId ? JOB_POLL_MS : 0,
  })
  const [results, setResults] = useState<PartnershipLaunchTargetResultDto[]>([])
  const [jobError, setJobError] = useState<string | null>(null)

  const confirm = useCallback(async () => {
    setError(null)
    try {
      const { jobRunId, targets } = await launchAds(buildRequest()).unwrap()
      setAccepted(targets)
      setJobId(jobRunId)
      setStep("creating")
    } catch (e) {
      setError(getServerErrorMessage(e, LAUNCH_FAILED))
    }
  }, [launchAds, buildRequest])

  const onLaunchedRef = useRef(onLaunched)
  useEffect(() => {
    onLaunchedRef.current = onLaunched
  }, [onLaunched])

  useEffect(() => {
    const status = job?.status
    if (!jobId || !status || ACTIVE_JOB_STATUSES.includes(status)) return
    setJobId(null)
    setStep("done")
    const outcomes = readLaunchResults(job?.result)
    setResults(outcomes)
    if (status === "COMPLETED") {
      const counts = summarizeLaunch(job?.result)
      toast.success(
        counts
          ? createdAdsToast(counts.created, counts.failed)
          : "Paused ads created",
      )
      const failed = outcomes.filter(r => !r.ok)
      if (failed.length > 0) {
        toast.error(
          failed
            .map(f => `${labelFor(f)}: ${describeLaunchFailure(f)}`)
            .join("\n"),
        )
      }
    } else {
      const message = job?.error ?? null
      setJobError(message)
      toast.error(
        `Launch ${status.toLowerCase()}${message ? `: ${message}` : "."}`,
      )
    }
    onLaunchedRef.current()
    // Fire only on the status transition — the result arrives in the same
    // response that flips it terminal (same pattern as the tab's sync poll).
  }, [job?.status])

  const handleClose = useCallback(() => {
    if (step === "creating") {
      toast.info(LAUNCH_IN_BACKGROUND_NOTE)
      onLaunchedRef.current()
    }
    onClose()
  }, [step, onClose])

  const goTo = (to: Step) => {
    setError(null)
    setStep(to)
  }

  const spinner = <span className="loading loading-spinner loading-xs" />

  let adSetList: ReactNode
  if (adSetsLoading) {
    adSetList = (
      <div className="py-system-md text-center text-sm text-gray-300">
        Loading ad sets…
      </div>
    )
  } else if (groups.length === 0) {
    adSetList = (
      <div className="py-system-md text-center text-sm text-gray-300">
        {search ? `No ad sets match "${search}".` : "No ad sets available."}
      </div>
    )
  } else {
    adSetList = (
      <ul className="flex flex-col">
        {groups.map((campaign, campaignIdx) => (
          <li
            key={campaign.id}
            className={cn(campaignIdx !== 0 && "border-t border-basic-blue")}
          >
            <div className="px-system-sm py-system-xs text-xs font-semibold uppercase tracking-wide text-gray-200">
              {campaign.name}
            </div>
            <ul className="flex flex-col">
              {campaign.adSets.map(adSet => {
                const id = String(adSet.id)
                const isSelected = selectedAdSets.has(id)
                const disabled = atCap && !isSelected
                const status = adSet.effective_status ?? adSet.status
                return (
                  <li key={id}>
                    <label
                      className={cn(
                        "flex w-full cursor-pointer items-center gap-2 ps-system-lg pe-system-sm py-system-2xs hover:bg-basic-dark-blue/40",
                        isSelected && "bg-basic-dark-blue/70",
                        disabled && "cursor-not-allowed opacity-50",
                      )}
                    >
                      <Checkbox
                        aria-label={`Select ad set ${adSet.name}`}
                        checked={isSelected}
                        disabled={disabled}
                        onChange={() => toggleAdSet(adSet)}
                      />
                      <span
                        className="min-w-0 flex-1 truncate text-sm text-white"
                        title={adSet.name}
                      >
                        {adSet.name}
                      </span>
                      {status && (
                        <StatusChip tone={adSetStatusTone(status)}>
                          {humanize(status)}
                        </StatusChip>
                      )}
                      {adSet.optimization_goal && (
                        <span
                          className="shrink-0 whitespace-nowrap text-xs text-gray-400"
                          title="Optimization goal"
                        >
                          {humanize(adSet.optimization_goal)}
                        </span>
                      )}
                    </label>
                  </li>
                )
              })}
            </ul>
          </li>
        ))}
      </ul>
    )
  }

  let footer: ReactNode
  switch (step) {
    case "adsets":
      footer = (
        <>
          <button
            type="button"
            onClick={handleClose}
            className="btn btn-ghost btn-sm"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => goTo("details")}
            disabled={selectedAdSets.size === 0}
            className="btn btn-primary btn-sm"
          >
            Next
          </button>
        </>
      )
      break
    case "details":
      footer = (
        <>
          <button
            type="button"
            onClick={() => goTo("adsets")}
            className="btn btn-ghost btn-sm"
          >
            Back
          </button>
          <button
            type="button"
            onClick={() => void runPreview()}
            disabled={previewing}
            className="btn btn-primary btn-sm"
          >
            {previewing && spinner}
            Preview
          </button>
        </>
      )
      break
    case "preview":
      footer = (
        <>
          <button
            type="button"
            onClick={() => goTo("details")}
            disabled={launching}
            className="btn btn-ghost btn-sm"
          >
            Back
          </button>
          <button
            type="button"
            onClick={() => void confirm()}
            disabled={!preview || preview.launchable === 0 || launching}
            className="btn btn-primary btn-sm"
          >
            {launching && spinner}
            {CREATE_PAUSED_ADS_LABEL}
          </button>
        </>
      )
      break
    case "creating":
      footer = (
        <button
          type="button"
          onClick={handleClose}
          className="btn btn-ghost btn-sm"
        >
          Close
        </button>
      )
      break
    case "done":
      footer = (
        <button
          type="button"
          onClick={onClose}
          className="btn btn-primary btn-sm"
        >
          Done
        </button>
      )
      break
  }

  // Request-level cause, stated once above the target list (each blocked
  // row also lists it among its blockers).
  const missingRequiredTagsLine = preview
    ? describeMissingRequiredTags(preview)
    : null

  return (
    <Dialog
      isOpen
      handleClose={handleClose}
      closeOnClickOutside={step !== "creating"}
      width={640}
      classes={{ container: DIALOG_CONTAINER_CLS }}
    >
      <div>
        <h2 className="text-lg font-semibold">{launchDialogTitle(rows)}</h2>
        <p className="mt-1 text-xs text-gray-400">{LAUNCH_STEP_COPY[step]}</p>
      </div>

      {step === "adsets" && (
        <div className="flex flex-col gap-2">
          <Input
            fullWidth
            type="search"
            aria-label="Search ad sets"
            placeholder="Search ad sets or campaigns…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            prependNode={
              <MagnifyingGlassIcon className="h-4 w-4 text-gray-300" />
            }
          />
          <div className="max-h-80 overflow-y-auto rounded-md border border-basic-blue bg-basic-black">
            {adSetList}
          </div>
          <p
            className="text-xs text-gray-400"
            data-testid="launch-adset-summary"
          >
            {selectedAdSets.size} of up to {adSetCap} ad sets selected ·{" "}
            {plural(adCount, "paused ad")} will be created
          </p>
        </div>
      )}

      {step === "details" && (
        <div className="flex flex-col gap-3">
          <Field
            label="Ad name"
            hint={single ? `up to ${AD_NAME_MAX} characters` : undefined}
          >
            {single ? (
              <Input
                fullWidth
                aria-label="Ad name"
                value={adNameValue}
                placeholder={
                  hasNameTemplate ? AD_NAME_CONVENTION_PLACEHOLDER : undefined
                }
                maxLength={AD_NAME_MAX}
                onChange={e => {
                  setAdName(e.target.value)
                  setAdNameTouched(true)
                }}
              />
            ) : (
              <p className="text-sm text-gray-300" data-testid="bulk-ad-name">
                {hasNameTemplate
                  ? BULK_AD_NAME_CONVENTION_NOTE
                  : BULK_AD_NAME_NOTE}
              </p>
            )}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setBuilderOpen(true)}
                className="text-xs font-medium text-brand hover:underline"
              >
                {single ? "Build name from tags" : "Pick tags for every ad"}
              </button>
              {tagCount > 0 && (
                <span
                  className="text-xs text-gray-400"
                  data-testid="launch-tag-count"
                >
                  {plural(tagCount, "tag")} on every ad
                </span>
              )}
            </div>
          </Field>
          <Field label="Landing page" hint="https">
            <Input
              fullWidth
              type="url"
              aria-label="Landing page URL"
              placeholder={
                single
                  ? "https://…"
                  : "Each post's own landing page, else the company default"
              }
              value={landingValue}
              onChange={e => setLandingPage(e.target.value)}
            />
            {!single && (
              <p className="text-xs text-gray-500">
                Leave empty to use each post&apos;s landing page
                {defaults?.destinationUrl
                  ? `, else ${defaults.destinationUrl}`
                  : ""}
                .
              </p>
            )}
          </Field>
          <Field label="Call to action">
            <p className="text-sm text-white" data-testid="launch-cta">
              {CTA_LABEL}
            </p>
          </Field>
          <p className="text-xs text-gray-400">
            {plural(rows.length, "post")} ×{" "}
            {plural(selectedAdSets.size, "ad set")} ={" "}
            {plural(adCount, "paused ad")}.
          </p>
        </div>
      )}

      {step === "preview" && preview && (
        <div className="flex flex-col gap-2">
          <p
            className="text-xs text-gray-400"
            data-testid="launch-preview-summary"
          >
            {preview.launchable} of {plural(preview.targets.length, "target")}{" "}
            pass{preview.launchable === 1 ? "es" : ""} Meta&apos;s dry run.
            {/* Nothing passing keeps Confirm disabled, so no note then. */}
            {preview.launchable > 0 &&
              preview.launchable < preview.targets.length &&
              ` ${LAUNCH_PREVIEW_REST_NOTE}`}
          </p>
          {missingRequiredTagsLine && (
            <p
              className="text-xs text-system-red-400"
              data-testid="launch-preview-required-tags"
            >
              {missingRequiredTagsLine}
            </p>
          )}
          <ul className="max-h-80 divide-y divide-basic-blue overflow-y-auto rounded-md border border-basic-blue bg-basic-black">
            {preview.targets.map(target => {
              // The API puts the required-tags sentence in both the
              // blockers slot and the validation slot; the blockers line
              // above already carries it, so don't print it twice.
              const why = target.blockers.includes("required_tags_missing")
                ? null
                : describeValidation(target.validation)
              // The code blocker is Meta's call: the chip, `why` and the
              // creator steps already say how that went.
              const blockers = hardLaunchBlockers(target.blockers)
              return (
                <li
                  key={targetKey(target)}
                  className="flex flex-col gap-1 px-system-sm py-system-xs"
                  data-testid={`launch-target-${target.contentId}-${target.adsetId}`}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className="min-w-0 flex-1 truncate text-sm text-white"
                      title={target.adName}
                    >
                      {target.adName}
                    </span>
                    <TargetChip target={target} />
                  </div>
                  <span
                    className="truncate text-xs text-gray-400"
                    title={`${target.campaignName ?? "Unknown campaign"} › ${target.adsetName ?? target.adsetId} · ${target.landingPageUrl ?? "no landing page"}`}
                  >
                    {target.campaignName ?? "Unknown campaign"} ›{" "}
                    {target.adsetName ?? target.adsetId} ·{" "}
                    {target.landingPageUrl ?? "no landing page"} · {CTA_LABEL}
                  </span>
                  {blockers.length > 0 && (
                    <span className="text-xs text-system-red-400">
                      {describeTargetBlockers({ ...target, blockers })}
                    </span>
                  )}
                  {why && (
                    <span className="text-xs text-system-red-400">{why}</span>
                  )}
                  {target.needsAdCode && (
                    <span className="text-xs text-system-yellow-200">
                      {AD_CODE_CREATOR_STEPS}
                    </span>
                  )}
                  {target.warnings.length > 0 && (
                    <span className="text-xs text-system-yellow-200">
                      {target.warnings
                        .map(w => LAUNCH_WARNING_COPY[w])
                        .join(" · ")}
                    </span>
                  )}
                </li>
              )
            })}
          </ul>
        </div>
      )}

      {step === "creating" && (
        <div
          role="status"
          className="flex items-center gap-2 py-system-md text-sm text-gray-300"
        >
          {spinner}
          {creatingPausedAdsLabel(accepted)}
          {job?.step ? ` · ${job.step}` : ""}…
          <span className="text-xs text-gray-500">
            You can close this; {LAUNCH_IN_BACKGROUND_NOTE.toLowerCase()}
          </span>
        </div>
      )}

      {step === "done" && (
        <div className="flex flex-col gap-2">
          {jobError && <p className="text-xs text-states-error">{jobError}</p>}
          {results.length === 0 ? (
            <p className="text-sm text-gray-300">
              The job finished. Refresh from Meta if the new ads are not in the
              Ads column yet.
            </p>
          ) : (
            <ul className="max-h-80 divide-y divide-basic-blue overflow-y-auto rounded-md border border-basic-blue bg-basic-black">
              {results.map(result => (
                <li
                  key={targetKey(result)}
                  className="flex flex-col gap-1 px-system-sm py-system-xs"
                  data-testid={`launch-result-${result.contentId}-${result.adsetId}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="min-w-0 flex-1 truncate text-sm text-white">
                      {labelFor(result)}
                    </span>
                    {result.ok ? (
                      <StatusChip tone="green">Created (paused)</StatusChip>
                    ) : (
                      <StatusChip tone="red">Failed</StatusChip>
                    )}
                  </div>
                  <span className="text-xs text-gray-400">
                    {result.ok
                      ? `Ad ${result.adId ?? "created"}${result.creativeId ? ` · creative ${result.creativeId}` : ""}`
                      : describeLaunchFailure(result)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {error && (
        <p className="text-xs text-states-error" role="alert">
          {error}
        </p>
      )}
      <div className="mt-2 flex items-center justify-end gap-2">{footer}</div>

      {/* Rendered inside the Dialog so its overlay shares the stacking context.
          The composed name only applies to a single row; bulk names are the
          server's. With a naming convention the composed name is the
          convention part: shown whole, as the server names the ad, and left
          to the server. The tag selection applies either way. */}
      {builderOpen && (
        <AdNameBuilder
          isOpen
          onClose={() => setBuilderOpen(false)}
          initialSelection={tags}
          onApply={(name, picked) => {
            if (single) {
              setAdName(
                (hasNameTemplate
                  ? composePartnershipAdName(single, name)
                  : name
                ).slice(0, AD_NAME_MAX),
              )
              setAdNameTouched(false)
            }
            setTags(picked)
          }}
        />
      )}
    </Dialog>
  )
}
